import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, OnDestroy } from "@angular/core";
import {
  AccountMethods,
  AccountJwt,
  AnyAccountJwtVO,
  Conf2AccountJwtVO,
  GuestAccountJwtVO,
  IAMAccountJwtVO,
  IdentityAccountJwtVO,
  KeycloakAccountJwtVO,
  AnyAccountJwtVOSpecs
} from "@common/domain";
import { jwtDecode } from "jwt-decode";
import { CookieOptions, CookieService } from "ngx-cookie";
import { BehaviorSubject, interval, Observable, Subject } from "rxjs";
import { distinctUntilChanged, map, takeUntil } from "rxjs/operators";
import { ACCOUNT_JWT_CONFIG, AccountJwtConfig } from "../providers";

@Injectable()
export class AccountJwtService implements OnDestroy {
  private static ACCOUNT_JWT_COOKIE_PATH = "/";

  private readonly _accountJwt$: BehaviorSubject<null | AccountJwt>;
  private readonly destroy$ = new Subject<void>();
  private readonly sessionStorage: globalThis.Storage;

  anyAccountJwtVO$: Observable<null | AnyAccountJwtVO>;
  identityAccountJwtVO$: Observable<null | IdentityAccountJwtVO>;

  constructor(
    @Inject(ACCOUNT_JWT_CONFIG)
    private readonly accountJwtConfig: AccountJwtConfig,
    private readonly cookieService: CookieService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.sessionStorage = this.document.defaultView!.sessionStorage;

    this._accountJwt$ = new BehaviorSubject<null | AccountJwt>(
      this.accountJwtGet()
    );

    this.accountJwtObserve$().pipe(takeUntil(this.destroy$)).subscribe();

    this.anyAccountJwtVO$ = this._accountJwt$.pipe(
      distinctUntilChanged(),
      map((_) => (_ ? this.anyAccountJwtCreate(_) : null))
    );
    this.identityAccountJwtVO$ = this.anyAccountJwtVO$.pipe(
      map((_) =>
        !!_ && AnyAccountJwtVOSpecs.isIdentityAccountJwtVO(_)
          ? (_ as IdentityAccountJwtVO)
          : null
      )
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this._accountJwt$.complete();
  }

  get accountJwt(): null | AccountJwt {
    return this._accountJwt$.value;
  }

  get accountJwt$(): Observable<null | AccountJwt> {
    return this._accountJwt$.pipe(distinctUntilChanged());
  }

  accountJwtDelete() {
    const { name, domain } = this.accountJwtConfig;

    this.sessionStorage.removeItem(name);
    this.cookieService.remove(name, {
      domain,
      path: AccountJwtService.ACCOUNT_JWT_COOKIE_PATH
    });

    this._accountJwt$.next(null);
  }

  accountJwtSet(jwt: AccountJwt, sessionStorage = false) {
    const { name, domain } = this.accountJwtConfig;

    if (sessionStorage) {
      this.sessionStorage.setItem(name, jwt);
      this._accountJwt$.next(jwt);

      return;
    }

    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

    const options: CookieOptions = {
      expires: oneYearFromNow,
      path: AccountJwtService.ACCOUNT_JWT_COOKIE_PATH,
      domain
    };

    this.cookieService.put(name, jwt, options);
    this._accountJwt$.next(jwt);
  }

  private accountJwtGet() {
    const { name } = this.accountJwtConfig;

    const accountJwt =
      this.sessionStorage.getItem(name) || this.cookieService.get(name);

    return accountJwt ? (accountJwt as AccountJwt) : null;
  }

  private accountJwtObserve$(): Observable<void> {
    return interval(500).pipe(
      map(() => this.accountJwtGet()),
      distinctUntilChanged(),
      map((_) => this._accountJwt$.next(_))
    );
  }

  private anyAccountJwtCreate(anyAccountJwt: AccountJwt): AnyAccountJwtVO {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload = jwtDecode<any>(anyAccountJwt);

    switch (payload["method"]) {
      case AccountMethods.Guest:
        return new GuestAccountJwtVO(payload);
      case AccountMethods.IAM:
        return new IAMAccountJwtVO(payload);
      case AccountMethods.Keycloak:
        return new KeycloakAccountJwtVO(payload);
      case AccountMethods.Conf2:
        return new Conf2AccountJwtVO(payload);
      default:
        throw new Error("Invalid token method");
    }
  }
}
